import { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
  Link,
} from '@mui/material';
import { useTranslate, useNotify, useRedirect, useDataProvider } from 'react-admin';
import TelegramIcon from '@mui/icons-material/Telegram';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ChannelType } from '../../../types/chat.types';
import { QueueSelector } from '../../queue/QueueSelector';

interface TelegramWizardProps {
  onBack: () => void;
}

const steps = ['channelInfo', 'botSetup', 'finish'];

export const TelegramWizard = ({ onBack }: TelegramWizardProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [botToken, setBotToken] = useState('');
  const [defaultQueueId, setDefaultQueueId] = useState<number | null>(null);
  const [createdChannelId, setCreatedChannelId] = useState<number | null>(null);

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validate channel name
      if (!channelName.trim()) {
        notify('resources.channel.wizard.errors.nameRequired', { type: 'error' });
        return;
      }
      if (!defaultQueueId) {
        notify('resources.channel.wizard.errors.queueRequired', { type: 'error' });
        return;
      }
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Create the channel with bot token
      if (!botToken.trim()) {
        notify('resources.channel.wizard.errors.botTokenRequired', { type: 'error' });
        return;
      }

      setLoading(true);
      try {
        const { data } = await dataProvider.create('channel', {
          data: {
            name: channelName,
            type: ChannelType.TELEGRAM,
            defaultQueueId,
            credentials: {
              botToken: botToken,
            },
          },
        });
        setCreatedChannelId(data.id);
        setActiveStep(2);
        notify('resources.channel.wizard.success.channelCreated', { type: 'success' });
      } catch (error) {
        notify('resources.channel.wizard.errors.createFailed', { type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      onBack();
    }
  };

  const handleFinish = () => {
    if (createdChannelId) {
      redirect('show', 'channel', createdChannelId);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        {translate('ra.action.back')}
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'rgba(0, 136, 204, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TelegramIcon sx={{ fontSize: 28, color: '#0088CC' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="medium">
              {translate('resources.channel.wizard.telegram.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.channel.wizard.telegram.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step) => (
            <Step key={step}>
              <StepLabel>
                {translate(`resources.channel.wizard.steps.${step}`)}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {translate('resources.channel.wizard.telegram.step1Description')}
            </Typography>

            <TextField
              fullWidth
              label={translate('resources.channel.fields.name')}
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder={translate('resources.channel.wizard.telegram.namePlaceholder')}
              helperText={translate('resources.channel.wizard.telegram.nameHelper')}
              sx={{ mb: 3 }}
              required
            />

            <QueueSelector
              value={defaultQueueId}
              onChange={setDefaultQueueId}
              required
              allowClear={false}
              helperText={translate('resources.channel.wizard.queueHelper')}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!channelName.trim() || !defaultQueueId}
              >
                {translate('ra.action.next')}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom fontWeight="medium">
                {translate('resources.channel.wizard.telegram.howToGetToken')}
              </Typography>
              <Typography variant="body2" component="div">
                1. {translate('resources.channel.wizard.telegram.step1_open')}{' '}
                <Link href="https://t.me/BotFather" target="_blank" rel="noopener">
                  @BotFather
                </Link>
              </Typography>
              <Typography variant="body2">
                2. {translate('resources.channel.wizard.telegram.step2_newbot')}
              </Typography>
              <Typography variant="body2">
                3. {translate('resources.channel.wizard.telegram.step3_copy')}
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label={translate('resources.channel.wizard.telegram.botToken')}
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
              helperText={translate('resources.channel.wizard.telegram.tokenHelper')}
              type="password"
              sx={{ mb: 3 }}
              required
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleBack}>
                {translate('ra.action.back')}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || !botToken.trim()}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  translate('resources.channel.wizard.createChannel')
                )}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {translate('resources.channel.wizard.telegram.successTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {translate('resources.channel.wizard.telegram.successMessage')}
            </Typography>

            <Button variant="contained" onClick={handleFinish}>
              {translate('resources.channel.wizard.telegram.goToChannel')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
